<template>
    <drop-pane-button icon="tint" :panename="name" ref="colourButton" :tabindex="tabindex"
            class="editbutton" :disabled="disabled" @show="onShow" :triggerHide="triggerHide">
        <template v-slot:icon>
            <slot></slot>
        </template>
        <div class="palettewrap">
            <div class="palette" @keydown="onKeyDown" ref="palette" tabindex="0">
                <div v-for="(r, ind) in palette" :key="`r-${ind}`" :class="`paletterow rownum_${ind}`">
                    <div v-for="(c, ind2) in r" :key="`r-${ind}-c-${ind2}`"
                            :class="{ palettecell: true, [`colnum_${ind2}`]: true, [`border_${c.border}`]: true,
                                selected: ((coords.x === ind2) && (coords.y === ind)) }"
                            :style="{ backgroundColor: c.colour }"
                            role="button" :aria-selected="((coords.x === ind2) && (coords.y === ind))"
                            :title="c.colour" @mouseover="onMouseOver(ind2, ind)" @click="selectColour">
                    </div>
                </div>
            </div>

            <div class="sample">
                <div class="text" :style="{ color: value }">
                    {{valueAsHex}}
                </div>
                <div class="block" :style="{ backgroundColor: value }"></div>
            </div>
        </div>
    </drop-pane-button>
</template>

<script>
import DropPaneButton from '@/components/dropPaneButton';
import Color from 'color';

export default {
  name: 'editorColourPicker',
  components: { DropPaneButton },
  data() {
    return {
      coords: { x: 0, y: 0 },
      value: 'rgb(0,0,0)',
      selectedValue: 'rgb(0,0,0)'
    };
  },
  props: {
    init: String,
    disabled: Boolean,
    name: String,
    triggerHide: Boolean,
    tabindex: {
      Type: [ Number, String ],
      default: 0
    }
  },
  methods: {
    onMouseOver(x, y) {
      this.coords = { x, y };
      this.value = this.palette[y][x].colour;
    },
    onKeyDown(evt) {
      if (evt.key === 'ArrowDown') {
        this.coords.y++;
      } else if (evt.key === 'ArrowUp') {
        this.coords.y--;
      } else if (evt.key === 'ArrowLeft') {
        this.coords.x--;
      } else if (evt.key === 'ArrowRight') {
        this.coords.x++;
      } else if (evt.key === 'Escape') {
        this.$refs.colourButton.hide();
      } else if ((evt.key === ' ') || (evt.key === 'Enter')) {
        this.selectColour();
      }
      evt.stopPropagation();

      if (this.coords.x < 0) {
        this.coords.x = 0;
      } else if (this.coords.x >= this.palette[0].length) {
        this.coords.x = this.palette[0].length - 1;
      } else if (this.coords.y < 0) {
        this.coords.y = 0;
      } else if (this.coords.y >= this.palette.length) {
        this.coords.y = this.palette.length - 1;
      }
      this.value = this.palette[this.coords.y][this.coords.x].colour;
    },
    selectColour() {
      this.selectedValue = this.value;
      this.$emit('select', this.selectedValue);
      console.log('select colour', this.selectedValue);
      this.$refs.colourButton.hide();
    },
    onShow() {
      this.value = this.init;
      this.$nextTick(() => this.$refs.palette.focus());
    },
    onToolbarAction(action, tb) {
      this.$refs.colourButton.onToolbarAction(action, tb);
    }
  },
  computed: {
    palette() {
      var starting = [[255, 0, 0], [255, 128, 0], [255, 255, 0], [0, 255, 0],
        [0, 255, 255], [0, 128, 255], [0, 0, 255], [128, 0, 255], [255, 0, 255]];
      var palette = [];
      const colourRows = 8;
      for (let row = 0; row < 2 + colourRows; row++) {
        palette[row] = [];
        if (row === 0) {
          for (let col = 0; col < starting.length; col++) {
            const l = Math.floor((255 * col) / (starting.length - 1));
            palette[row][col] = `rgb(${l},${l},${l})`;
          }
        } else if (row === 1) {
          for (let col = 0; col < starting.length; col++) {
            palette[row][col] = `rgb(${starting[col][0]},${starting[col][1]},${starting[col][2]})`;
          }
        } else {
          const midpoint = (colourRows + 1) / 2;
          for (let col = 0; col < starting.length; col++) {
            let colour = Color.rgb(starting[col]);
            const l = (row - 2) - midpoint;
            if (l < 0) {
              const mod = -l / (midpoint + 1);
              colour = colour.lighten(mod);
            } else if (l > 0) {
              const mod = l / (midpoint + 1);
              colour = colour.darken(mod);
            }
            colour = colour.rgb().array();
            colour = colour.map(c => Math.round(c));
            palette[row][col] = `rgb(${colour[0]},${colour[1]},${colour[2]})`;
          }
        }
      }
      palette = palette.map(r => r.map(c => {
        return { colour: c, border: Color(c).luminosity() > 0.5 ? 'dark' : 'light' };
      }));
      return palette;
    },
    valueAsHex() {
      return Color(this.value).hex();
    }
  }
};
</script>

<style scoped lang="scss">
    @import '../assets/bootstrap-style.scss';

    .palette {
        margin: 10px 10px 0 10px;
        .paletterow {
            height: 15px;
            white-space: nowrap;

            &.rownum_0, &.rownum_1 {
                margin-bottom: 5px;
            }

            .palettecell {
                padding: 0;
                margin: 0;
                width: 15px;
                height: 15px;
                display: inline-block;
                box-sizing: border-box;;
                &.selected {
                    border: 1px solid $black;
                }
                &.border_light.selected {
                    border: 1px solid $gray-200
                }
            }
        }
    }
    .sample {
        padding: 0;
        margin: 10px 10px 0 10px;
        width: calc(100% - 20px);
        font-size: 0.8em;
        height: 1.5em;
        display: flex;
        .text {
            flex: 1;
            transition: color 0.3s;
        }
        .block {
            flex: 1;
            height: calc(100% - 5px);
            transition: background-color 0.3s;

        }
    }

</style>
